import { useCallback, useContext, useEffect, useState } from "react";

import AsyncSelect from "react-select/async";
import { H3 } from "@adfenix/alexandria";
import { ReadLayerContext } from "./ReadLayerQueryContext";
import { getReadLayerSuggestions } from "api/services";
import { useTranslation } from "react-i18next";

export interface OptionType {
  [key: string]: string;
}

export interface ColumnSearchProps {
  column: string;
  label?: string;
  placeholder?: string;
  showLabel?: boolean;
  showDefaultOptions?: boolean;
  filterColumn?: string;
  useCascadeData?: boolean;
  onChange?: (value: string) => void;
  rememberValue?: boolean;
}

export const ColumnSearch = ({
  column,
  label,
  placeholder,
  showLabel,
  showDefaultOptions = true,
  filterColumn,
  useCascadeData = false,
  onChange,
  rememberValue = true
}: ColumnSearchProps) => {
  if (!filterColumn && useCascadeData)
    throw "ussing cascade data without filter column is not possible";

  const { t } = useTranslation();
  const [defaultOptions, setDefaultOptions] = useState<OptionType[]>([]);
  const [shouldLoadOptions, setShouldLoadOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const ctx = useContext(ReadLayerContext);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { query, addFilter } = ctx!;

  const getSuggestion = useCallback(
    (inputValue: string): Promise<OptionType[]> =>
      new Promise(resolve => {
        const filters = filterColumn
          ? query.filters.filter(x => x.column === filterColumn)
          : [];

        const resultPromise = getReadLayerSuggestions({
          index: query.index,
          service: query.service,
          size: 10,
          searchTerm: inputValue,
          columns: [column],
          filters
        });

        resultPromise.then(x => {
          const result = x[0];
          resolve(
            result.values.map(x => {
              return {
                label: x.value,
                value: x.value
              };
            })
          );
        });
      }),
    [query, column, filterColumn]
  );

  const loadDefaults = () => {
    setIsLoading(true);
    const resultPromise = getSuggestion("");
    resultPromise.then(x => {
      setDefaultOptions(x);
      setIsLoading(false);
    });
  };

  const getDefaultSuggestionsLazy = useCallback(() => {
    if (
      shouldLoadOptions &&
      showDefaultOptions &&
      defaultOptions.length === 0 &&
      !useCascadeData
    ) {
      loadDefaults();
    }
  }, [showDefaultOptions, getSuggestion, shouldLoadOptions, useCascadeData]);

  useEffect(() => {
    getDefaultSuggestionsLazy();
  }, [getDefaultSuggestionsLazy]);

  const getCascadeSuggestionData = useCallback(() => {
    if (query && filterColumn && useCascadeData && shouldLoadOptions) {
      const filters = filterColumn
        ? query.filters.filter(x => x.column === filterColumn)
        : [];

      if (filters.length > 0 || showDefaultOptions) {
        loadDefaults();
      } else {
        setDefaultOptions([]);
      }
    }
  }, [
    query,
    filterColumn,
    useCascadeData,
    showDefaultOptions,
    getSuggestion,
    shouldLoadOptions
  ]);

  useEffect(() => {
    getCascadeSuggestionData();
  }, [getCascadeSuggestionData]);

  if (!ctx) return null;

  const propertyStatusMap: OptionType = {
    upcoming: t("listings.property.listing.status.upcoming"),
    forsale: t("listings.listing.status.forSale"),
    sold: t("listings.listing.status.sold"),
    removed: t("listings.listing.status.removed")
  };

  const officeStatusMap: OptionType = {
    true: t("listings.filters.office.status.active"),
    false: t("listings.filters.office.status.inactive")
  };

  const mapLabel = (value: string) => {
    if (column === "propertyStatusText") {
      return propertyStatusMap[value];
    }
    if (column === "office.active") {
      return officeStatusMap[value];
    }
    return value;
  };

  const loadOptions = (inputValue: string): Promise<OptionType[]> =>
    new Promise(resolve => {
      const resultPromise = getSuggestion(inputValue);
      resultPromise.then(x => {
        resolve(
          x.map(x => {
            return {
              label: mapLabel(x.value),
              value: x.value
            };
          })
        );
      });
    });

  // eslint-disable-next-line
  const updateOption = (option: any) => {
    addFilter(
      {
        column,
        value: option?.value,
        displayValue: option?.value
      },
      !rememberValue
    );

    if (onChange) onChange(option?.value);
  };

  const onFocus = () => {
    if (shouldLoadOptions || !showDefaultOptions) return;

    setShouldLoadOptions(true);
  };

  return (
    <>
      {showLabel && <H3>{label ?? column}</H3>}
      <AsyncSelect<OptionType, false>
        placeholder={placeholder}
        loadOptions={loadOptions}
        value={
          !rememberValue ||
          query.filters.find(x => x.column === column) == undefined
            ? null
            : {
                label: mapLabel(
                  query.filters.find(x => x.column === column)?.value ?? ""
                ),
                value: query.filters.find(x => x.column === column)?.value ?? ""
              }
        }
        defaultOptions={
          useCascadeData || showDefaultOptions
            ? defaultOptions.map(x => {
                return {
                  label: mapLabel(x.value),
                  value: x.value
                };
              })
            : false
        }
        isClearable={rememberValue}
        noOptionsMessage={_ => t("listings.filters.autocomplete.placeholder")}
        onChange={updateOption}
        onFocus={onFocus}
        isLoading={isLoading}
      />
    </>
  );
};
